import { growthFactor, getMaxMonthlyBenefitAmount } from '../sharedPolicyConfig';

// ---------------------------------------------------------------------------
// Shared duration/benefit-period pool-model helpers for the two-pool hybrid
// LTC strategies (OneAmerica Asset Care, NYL Asset Flex). Both strategies size
// their Acceleration (AOB/base) and Continuation/Extension (COB/EOB) pools the
// same way and compute the same forward-summed Total LTC Benefit, so these
// helpers live here once instead of being duplicated per strategy.
// ---------------------------------------------------------------------------

// Asset Care at-issue pool sizing. Prefer the benefit-period model
// (monthlyBenefit × benefit-period months) when BOTH
// policyAccelerationBenefitDurationMonths (AOB) and
// policyEobBenefitDurationMonths (COB) are set; otherwise fall back to the
// legacy dollar inputs (Face Amount, Continuation of Benefits) so existing
// policies keep producing their prior numbers. The gate is all-or-nothing
// (P2-13, mirroring NYL Asset Flex's deriveAtIssuePools): a partially-migrated
// row — exactly one benefit-period field set — stays fully on legacy behavior
// and raises a `dataWarnings` entry, because mixing a benefit-period-derived
// pool with a forward-inflated legacy dollar in one policy silently combines
// two models (and re-layers inflation on an already-inflated dollar). Sizing
// uses the policy's max monthly benefit (same source the cap math derives
// from), so the AOB/COB pools and the monthly cap inflate in lock-step —
// inflation grows the monthly benefit, NOT a pre-inflated dollar pool.
export function deriveAtIssuePools(ltcPolicy: PolicyFundingSource): {
  baseAtIssue: number;
  eobAtIssue: number;
  usesDurationModel: boolean;
  hasMixedModelFields: boolean;
} {
  const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
  const accelMonths = ltcPolicy.policyAccelerationBenefitDurationMonths;
  const eobMonths = ltcPolicy.policyEobBenefitDurationMonths;
  const usesDurationModel = accelMonths != null && eobMonths != null && baseMonthly > 0;
  const hasMixedModelFields = (accelMonths != null) !== (eobMonths != null);
  const baseAtIssue = usesDurationModel
    ? baseMonthly * accelMonths
    : ltcPolicy.policyMaximumBenefitAmount ?? 0;
  const eobAtIssue = usesDurationModel
    ? baseMonthly * eobMonths
    : ltcPolicy.policyContinuationBenefitAmount ?? 0;
  return { baseAtIssue, eobAtIssue, usesDurationModel, hasMixedModelFields };
}

// Total dollars paid over the full AOB + COB benefit period for a claim that
// STARTS at the beginning of `claimStartPolicyYear`, with the monthly benefit
// inflating each policy anniversary (capped at the inflation cap years). The
// AOB months inflate on the AOB rate; the COB months — which begin once the AOB
// months are spent — inflate on the COB rate, with the inflation clock running
// continuously from policy issue. This is the carrier's forward-summed "Total
// LTC Benefit" column: it exceeds the current pool principal by the inflation
// that accrues DURING the claim. Only meaningful under the benefit-period model.
// Honors the simple-vs-compound election through the shared `growthFactor`, so
// simple-inflation policies get a real forward total instead of an omitted one.
export function forwardBenefitPeriodTotal(
  ltcPolicy: PolicyFundingSource,
  claimStartPolicyYear: number,
  baseMonthly: number,
  baseRate: number,
  eobRate: number,
  capYears: number,
  isSimple: boolean,
): number {
  const accelMonths = ltcPolicy.policyAccelerationBenefitDurationMonths ?? 0;
  const eobMonths = ltcPolicy.policyEobBenefitDurationMonths ?? 0;
  let total = 0;
  for (let m = 0; m < accelMonths; m += 1) {
    const py = claimStartPolicyYear + Math.floor(m / 12);
    total += baseMonthly * growthFactor(baseRate, capYears, isSimple, py);
  }
  for (let k = 0; k < eobMonths; k += 1) {
    const py = claimStartPolicyYear + Math.floor((accelMonths + k) / 12);
    total += baseMonthly * growthFactor(eobRate, capYears, isSimple, py);
  }
  return total;
}
